import { useEffect, useState, useCallback } from 'react';
import { alerts, type Alarm } from '../api';

/**
 * Offene Alarme.
 *
 * Es gab bisher keine Ansicht dafür — die Alarme liefen auflaufend in die
 * Datenbank und wurden nur per Mail und Slack zugestellt. Wer wissen wollte,
 * was offen ist, musste seinen Posteingang durchsuchen.
 *
 * Die Stummschaltung ist bewusst **befristet**: eine ohne Ende wäre eine
 * abgeschaltete Überwachung, die niemand mehr einschaltet.
 */
export default function AlarmePanel() {
  const [liste, setListe] = useState<Alarm[]>([]);
  const [kanaele, setKanaele] = useState<{ slack: boolean; email: boolean }>({
    slack: false,
    email: false,
  });
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [nurOffene, setNurOffene] = useState(true);

  const holen = useCallback(async () => {
    try {
      const d = await alerts.list({ offen: nurOffene });
      setListe(d.alerts);
      setKanaele(d.kanaele);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLaedt(false);
    }
  }, [nurOffene]);

  useEffect(() => {
    holen();
    const t = setInterval(holen, 60_000);
    return () => clearInterval(t);
  }, [holen]);

  if (laedt) return <div className="text-sm text-slate-500">Wird geladen …</div>;

  const farbe = (s: string) =>
    s === 'critical'
      ? 'border-red-200 bg-red-50'
      : s === 'warning'
        ? 'border-amber-200 bg-amber-50'
        : 'border-slate-200 bg-white';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={nurOffene}
            onChange={(e) => setNurOffene(e.target.checked)}
          />
          nur offene
        </label>

        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span>
            Zustellung:{' '}
            <span className={kanaele.slack ? 'text-teal-700' : 'text-slate-400'}>
              Slack {kanaele.slack ? '✓' : '—'}
            </span>{' '}
            ·{' '}
            <span className={kanaele.email ? 'text-teal-700' : 'text-slate-400'}>
              Mail {kanaele.email ? '✓' : '—'}
            </span>
          </span>
          <button
            onClick={async () => {
              try {
                await alerts.test();
                setFehler(null);
              } catch {
                setFehler('Probenachricht fehlgeschlagen');
              }
            }}
            className="text-slate-600 underline hover:text-slate-900"
            title="Ein Kanal, der erst im Ernstfall benutzt wird, ist keiner."
          >
            Probenachricht
          </button>
        </div>
      </div>

      {fehler && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {fehler}
        </div>
      )}

      {liste.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-4 text-sm text-slate-500">
          {nurOffene ? 'Nichts offen.' : 'Keine Alarme aufgezeichnet.'}
        </div>
      ) : (
        liste.map((a) => {
          const stumm = a.silencedUntil && new Date(a.silencedUntil).getTime() > Date.now();
          return (
            <div key={a.id} className={`rounded-md border p-3 ${farbe(a.severity)}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800">{a.summary}</span>
                    {a.status === 'resolved' && (
                      <span className="rounded bg-teal-100 px-1.5 py-0.5 text-xs text-teal-800">
                        behoben
                      </span>
                    )}
                    {stumm && (
                      <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
                        stumm
                      </span>
                    )}
                  </div>
                  {a.description && (
                    <p className="mt-1 text-xs text-slate-600">{a.description}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-400">
                    {a.name} · seit {new Date(a.startsAt).toLocaleString('de-DE')}
                  </p>
                </div>

                <div className="shrink-0">
                  {stumm ? (
                    <button
                      onClick={async () => {
                        await alerts.unsilence(a.id).catch(() => undefined);
                        holen();
                      }}
                      className="text-xs text-slate-600 hover:text-slate-900"
                    >
                      Stummschaltung aufheben
                    </button>
                  ) : (
                    <select
                      defaultValue=""
                      onChange={async (e) => {
                        if (!e.target.value) return;
                        await alerts.silence(a.id, Number(e.target.value)).catch(() => undefined);
                        holen();
                      }}
                      className="rounded border border-slate-300 px-2 py-1 text-xs"
                    >
                      <option value="">stummschalten …</option>
                      <option value="1">1 Stunde</option>
                      <option value="4">4 Stunden</option>
                      <option value="24">1 Tag</option>
                      <option value="72">3 Tage</option>
                    </select>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
